import { useMemo, useState } from 'react';
import { Map as LeafletMap, LatLng } from 'leaflet';
import {
  buildTrackingBounds,
  Coordinate,
  transformLocationTrackingData,
  useGroundLegRoutes,
  useTrackingMapFit,
} from '@features/components/LocationTracking';
import { CaseFacility } from '@/api/apis/case';
import { DeviceTracking } from '@/api/apis/composer';

type UseLocationTrackingMapGeometryParams = {
  data: DeviceTracking | undefined;
  donorHospitalDetails: CaseFacility | undefined;
  txCenterDetails: CaseFacility | undefined;
};

export const useLocationTrackingMapGeometry = ({
  data,
  donorHospitalDetails,
  txCenterDetails,
}: UseLocationTrackingMapGeometryParams) => {
  const [map, setMap] = useState<LeafletMap | null>(null);

  const departureLat = donorHospitalDetails?.location?.latitude;
  const departureLng = donorHospitalDetails?.location?.longitude;
  const fallbackDeparture = useMemo<Coordinate | null>(
    () => (departureLat && departureLng ? { latitude: departureLat, longitude: departureLng } : null),
    [departureLat, departureLng],
  );

  const arrivalLat = txCenterDetails?.location?.latitude;
  const arrivalLng = txCenterDetails?.location?.longitude;
  const fallbackArrival = useMemo<Coordinate | null>(
    () => (arrivalLat && arrivalLng ? { latitude: arrivalLat, longitude: arrivalLng } : null),
    [arrivalLat, arrivalLng],
  );

  const groundRouteFallbacks = useGroundLegRoutes(data);

  const transformedData = useMemo(
    () => transformLocationTrackingData(data, fallbackDeparture, fallbackArrival, groundRouteFallbacks),
    [data, fallbackDeparture, fallbackArrival, groundRouteFallbacks],
  );

  const caseInfoMarkers = useMemo(() => {
    if (!fallbackDeparture || !fallbackArrival) return null;
    return {
      donorHospital: {
        id: 'donor-hospital',
        location: new LatLng(fallbackDeparture.latitude, fallbackDeparture.longitude),
      },
      txCenter: {
        id: 'tx-center',
        location: new LatLng(fallbackArrival.latitude, fallbackArrival.longitude),
      },
    };
  }, [fallbackDeparture, fallbackArrival]);

  const donorMarker = transformedData?.donorHospitalMarker ?? caseInfoMarkers?.donorHospital;
  const txCenterMarker = transformedData?.txCenterMarker ?? caseInfoMarkers?.txCenter;
  const isTrackingActive = !!transformedData;
  const activePosition = transformedData?.activePosition ?? null;

  const latLngBounds = useMemo(
    () =>
      buildTrackingBounds({
        donorMarker,
        txCenterMarker,
        legMarkers: transformedData?.legMarkers,
        activePosition,
      }),
    [donorMarker, txCenterMarker, transformedData?.legMarkers, activePosition],
  );

  useTrackingMapFit(map, latLngBounds, isTrackingActive);

  return { map, setMap, transformedData, donorMarker, txCenterMarker, isTrackingActive, activePosition, latLngBounds };
};
